import { useState, type FormEvent } from 'react'
import companyLogo from '../../../会社ロゴ.png'
import { usePageSeo } from '../../lib/seo'
import { saveInquiryItem } from '../../lib/siteContent'

const navigationItems = [
  { href: '/#about', label: '私たちについて' },
  { href: '/#services', label: 'サービス' },
  { href: '/#strengths', label: '強み' },
  { href: '/#company', label: '会社情報' },
]

export default function Contact() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitMessage, setSubmitMessage] = useState('')

  usePageSeo({
    title: 'お問い合わせ | 合同会社サクラネクサスメディア',
    description:
      '合同会社サクラネクサスメディアへのお問い合わせページです。商品企画、OEM製造、EC運営に関するご相談をメールフォームから受け付けています。',
    path: '/contact',
  })

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setIsSubmitting(true)
    setSubmitMessage('')

    const form = event.currentTarget
    const formData = new FormData(form)

    const name = formData.get('name') as string
    const company = (formData.get('company') as string) ?? ''
    const email = formData.get('email') as string
    const subject = (formData.get('subject') as string) ?? ''
    const message = formData.get('message') as string

    if (!name || !email || !message) {
      setSubmitMessage('必須項目を入力してください。')
      setIsSubmitting(false)
      return
    }

    if (message.length > 500) {
      setSubmitMessage('お問い合わせ内容は500文字以内でご入力ください。')
      setIsSubmitting(false)
      return
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d36khnrtg2u3f2oj3tvg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as never),
      })

      if (response.ok) {
        saveInquiryItem({ name, company, email, subject, message }, 'submitted')
        setSubmitMessage('お問い合わせを送信しました。内容を確認のうえ、順次ご連絡いたします。')
        form.reset()
      } else {
        saveInquiryItem({ name, company, email, subject, message }, 'failed')
        setSubmitMessage('送信に失敗しました。時間をおいて再度お試しください。')
      }
    } catch {
      saveInquiryItem({ name, company, email, subject, message }, 'failed')
      setSubmitMessage('送信に失敗しました。時間をおいて再度お試しください。')
    }

    setIsSubmitting(false)
  }

  return (
    <div className="bg-[#fcfeff] text-slate-700">
      <header className="sticky top-0 z-50 border-b border-sky-100 bg-white/88 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <a href="/" className="flex flex-col">
            <div>
              <p className="text-xs uppercase tracking-[0.35em] text-slate-400">Corporate Site</p>
              <p className="text-sm font-semibold text-slate-800">合同会社サクラネクサスメディア</p>
            </div>
          </a>

          <nav className="hidden items-center gap-7 lg:flex">
            {navigationItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="text-sm font-medium text-slate-500 transition hover:text-sky-700"
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="hidden items-center gap-3 lg:flex">
            <a href="#contact-form" className="brand-button">
              お問い合わせ
            </a>
          </div>

          <button
            type="button"
            onClick={() => setIsMenuOpen((open) => !open)}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-sky-100 bg-white/90 text-sky-700 lg:hidden"
            aria-label="メニューを開く"
          >
            <i className={`${isMenuOpen ? 'ri-close-line' : 'ri-menu-3-line'} text-xl`}></i>
          </button>
        </div>

        {isMenuOpen && (
          <div className="border-t border-sky-100 bg-white lg:hidden">
            <nav className="mx-auto flex max-w-7xl flex-col px-4 py-4 sm:px-6">
              {navigationItems.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={() => setIsMenuOpen(false)}
                  className="border-b border-sky-100 py-3 text-sm font-medium text-slate-600 last:border-b-0"
                >
                  {item.label}
                </a>
              ))}
            </nav>
          </div>
        )}
      </header>

      <main>
        <section className="section-light border-b border-sky-100 bg-[linear-gradient(180deg,#ffffff_0%,#f6fbff_100%)]">
          <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
            <p className="section-eyebrow">Contact</p>
            <h1 className="mt-4 max-w-4xl text-4xl font-semibold leading-tight text-slate-800 sm:text-5xl">お問い合わせはこちら</h1>
            <p className="mt-6 max-w-3xl text-base leading-8 text-slate-500">
              合同会社サクラネクサスメディアでは、商品企画、OEM製造、EC運営のご相談を中心に承っています。内容がまだ固まっていない段階でも問題ありません。
              まずはフォームから、現在の状況やご希望をお聞かせください。
            </p>
          </div>
        </section>

        <section className="section-light bg-white">
          <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
            <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr]">
              <div className="rounded-[2rem] border border-sky-100 bg-[#f7fbff] p-8">
                <h2 className="text-2xl font-semibold text-slate-800">お問い合わせについて</h2>
                <p className="mt-5 text-sm leading-8 text-slate-500">
                  初回のご連絡はメールフォームで承っています。ご相談内容を確認のうえ、通常1〜3営業日以内を目安に順次ご返信いたします。
                </p>
                <div className="mt-8 space-y-4">
                  <div className="rounded-2xl border border-sky-100 bg-white p-5">
                    <p className="text-xs uppercase tracking-[0.24em] text-slate-400">Scope</p>
                    <p className="mt-2 text-sm leading-7 text-slate-500">商品企画 / OEM製造 / ECサイト運営 / デジタル導線のご相談</p>
                  </div>
                  <div className="rounded-2xl border border-sky-100 bg-white p-5">
                    <p className="text-xs uppercase tracking-[0.24em] text-slate-400">Address</p>
                    <p className="mt-2 text-sm leading-7 text-slate-500">〒104-0061 東京都中央区銀座1丁目12番4号 N&E BLD.6F</p>
                  </div>
                  <div className="rounded-2xl border border-sky-100 bg-white p-5">
                    <p className="text-xs uppercase tracking-[0.24em] text-slate-400">Message</p>
                    <p className="mt-2 text-sm leading-7 text-slate-500">ご相談の背景や現状、検討中の内容が分かる範囲でご記入いただけると、よりスムーズにご案内できます。</p>
                  </div>
                  <div className="rounded-2xl border border-sky-100 bg-white p-5">
                    <p className="text-xs uppercase tracking-[0.24em] text-slate-400">Privacy</p>
                    <p className="mt-2 text-sm leading-7 text-slate-500">送信いただいた情報は、お問い合わせ対応の目的に限って利用します。</p>
                  </div>
                </div>
              </div>

              <div className="rounded-[2rem] border border-sky-100 bg-white p-8 shadow-[0_24px_70px_rgba(125,170,230,0.12)]">
                <h2 className="text-2xl font-semibold text-slate-800">お問い合わせフォーム</h2>
                <p className="mt-3 text-sm leading-7 text-slate-500">必要事項をご入力のうえ、送信してください。</p>
                <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="mt-8 space-y-5">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <label className="block">
                      <span className="form-label">お名前</span>
                      <input type="text" id="name" name="name" required className="form-input" placeholder="山田 太郎" />
                    </label>
                    <label className="block">
                      <span className="form-label">会社名</span>
                      <input type="text" id="company" name="company" className="form-input" placeholder="株式会社サンプル" />
                    </label>
                  </div>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <label className="block">
                      <span className="form-label">メールアドレス</span>
                      <input type="email" id="email" name="email" required className="form-input" placeholder="info@example.com" />
                    </label>
                    <label className="block">
                      <span className="form-label">お問い合わせ種別</span>
                      <div className="relative">
                        <select id="subject" name="subject" className="form-input appearance-none pr-10">
                          <option value="">選択してください</option>
                          <option value="商品企画の相談">商品企画の相談</option>
                          <option value="OEM製造の相談">OEM製造の相談</option>
                          <option value="EC運営の相談">EC運営の相談</option>
                          <option value="その他">その他</option>
                        </select>
                        <span className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-slate-400">
                          <i className="ri-arrow-down-s-line text-xl"></i>
                        </span>
                      </div>
                    </label>
                  </div>
                  <label className="block">
                    <span className="form-label">お問い合わせ内容</span>
                    <textarea
                      id="message"
                      name="message"
                      rows={6}
                      required
                      maxLength={500}
                      className="form-input min-h-[160px] resize-y"
                      placeholder="ご相談内容やご希望の進め方などをご記入ください。"
                    ></textarea>
                    <span className="mt-2 block text-xs text-slate-400">500文字以内でご入力ください。</span>
                  </label>

                  {submitMessage && (
                    <div
                      className={`rounded-2xl px-4 py-3 text-sm ${
                        submitMessage.includes('送信しました')
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'bg-rose-50 text-rose-700'
                      }`}
                    >
                      {submitMessage}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="brand-button w-full justify-center disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isSubmitting ? '送信中...' : 'お問い合わせを送信する'}
                  </button>
                  <p className="text-xs leading-6 text-slate-400">
                    送信いただいた内容は、お問い合わせ対応のためにのみ利用いたします。
                  </p>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-sky-100 bg-white">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-12 sm:px-6 lg:grid-cols-[1.15fr_0.85fr_0.8fr] lg:px-8">
          <div>
            <img src={companyLogo} alt="合同会社サクラネクサスメディア" className="h-12 w-auto" />
            <p className="mt-5 max-w-md text-sm leading-7 text-slate-500">
              合同会社サクラネクサスメディアは、デジタルマーケティング、OEM製造、ECサイト運営を通じて企業様の成長を支援します。
            </p>
          </div>
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-[0.24em] text-slate-400">Navigation</h3>
            <div className="mt-5 flex flex-col gap-3 text-sm text-slate-600">
              {navigationItems.map((item) => (
                <a key={item.href} href={item.href} className="transition hover:text-sky-700">
                  {item.label}
                </a>
              ))}
            </div>
          </div>
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-[0.24em] text-slate-400">Contact</h3>
            <div className="mt-5 space-y-3 text-sm leading-7 text-slate-600">
              <p>お問い合わせはメールフォームよりお願いいたします。</p>
              <p>〒104-0061 東京都中央区銀座1丁目12番4号 N&E BLD.6F</p>
            </div>
          </div>
        </div>
        <div className="border-t border-sky-100 px-4 py-6 text-center text-xs text-slate-400 sm:px-6 lg:px-8">
          © 2026 Sakuranexus Media LLC. All rights reserved.
        </div>
      </footer>
    </div>
  )
}
